<template>
	<div class="bg-color-white">
		<section class="marketing-section container">
			<div class="text-align-center">
				<h2 class="label-sm text-transform-uppercase text-color-content-secondary mb-150"
					>Advisory client performance</h2
				>
				<h3 class="heading-xl mb-100">Consistent growth over time</h3>
				<p class="mb-300 col-8-md col-offset-2-md">
					Fundrise investments are intended to be held long-term, as private investment funds take time to
					generate value. The data below supports the strategy that time on platform can significantly
					compound earnings, ultimately yielding the best returns for those who invest with a long-term
					perspective.
				</p>
			</div>
			<ClientReturnsChart />
			<p class="mt-150-md mt-300-smo mb-25 text-align-center muted body-xs">
				<BaseButton classes="text-link muted" @click="openModal"> Learn more </BaseButton>
				about the assumptions in this section. The foregoing performance information is presented solely with
				regard to the advisory client performance of the clients of Fundrise Advisors, LLC pursuant to the
				Investment Advisers Act of 1940, and does not represent the performance of any individual investor or
				any individual or aggregate performance of any funds with offerings qualified pursuant Regulation A of
				the Securities Act (the "Regulation A Funds"). For more information about the Regulation A Funds, and
				their corresponding Forms 1-A, including their individual performance information, please click
				<BaseButton
					classes="text-link muted"
					data-test="dividends-section-disclosure-prompt"
					@click="openDisclaimerModal()"
				>
					here</BaseButton
				>.
			</p>
			<BasePlaceHolder :placehold="isRelatedArticlesLoading" :line-count="6" class="mt-350">
				<div v-if="relatedArticlesSection.relatedArticles.length">
					<h2 class="label-sm text-transform-uppercase text-color-content-secondary">Letters to investors</h2>
					<p v-if="relatedArticlesSection.sectionHeading" class="heading-5 mt-25 mb-100">{{
						relatedArticlesSection.sectionHeading
					}}</p>

					<div class="display-flex overflow-scroll">
						<ArticleCard
							v-for="(article, articleId) in relatedArticlesSection.relatedArticles"
							:key="articleId"
							:classes="cssClasses(articleId)"
							:article="article"
							:show-date="true"
							:show-new-badge="true"
							:base-link-classes="'article-list-item-inner'"
						/>
					</div>
				</div>
			</BasePlaceHolder>
		</section>
	</div>
</template>
<script lang="ts" setup>
import ArticleCard from '@components/blog/article-card.vue';
import ClientReturnsChart from '@components/charts/client-returns-chart.vue';
import DisclaimerSimple from '@components/disclaimer/disclaimer-simple.vue';
import { regAFundListing } from '@constants/marketing-site';
import { SanityRelatedArticlesSection } from 'types/sanity/blog-education-center';
import { useAppStore } from '@stores/app';

interface Props {
	relatedArticlesSection: SanityRelatedArticlesSection;
	isRelatedArticlesLoading: boolean;
}

const props = defineProps<Props>();

interface Emits {
	(e: 'open-modal', value: object): void;
}

const emit = defineEmits<Emits>();

const appStore = useAppStore();

function cssClasses(articleId: number): string {
	let base = 'width-245 ';
	if (articleId !== props.relatedArticlesSection.relatedArticles.length - 1) {
		base += 'mr-250';
	}
	return base;
}

function openModal(modalProps: object): void {
	emit('open-modal', modalProps);
}

function openDisclaimerModal(): void {
	appStore.updateCurrentModal({
		modal: DisclaimerSimple,
		props: regAFundListing
	});
}
</script>
<style scoped lang="scss">
.width-245 {
	width: 245px;
	min-width: 245px;
}
</style>
